import React, { useState, useMemo } from 'react';
import Share from '../Share';
import AddToWishList from '../AddToWishList';
import Slideshow from '../Slideshow/Slideshow';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import endpoints from '../../define/endpoints';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { BREAKPOINTS, VIDEO_EXTENSIONS } from '../../../constants';
import HeadlineListing from './HeadlineListing';
import useGetVideoPreview from '../../hooks/useGetVideoPreview';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import {
  blockScrollDesktop,
  blockScrollMobile,
  restoreScrollDesktop,
  restoreScrollMobile,
} from '../../../widget/utils/blockScroll';

const CoverSwiper = ({
  listingId,
  title,
  shareText,
  coverImage,
  coverImagePrecrop,
  images,
  mosaic,
  listingAvailable,
  macroCategory,
  category,
}) => {
  const listingIsAvailable = parseInt(listingAvailable) > 0;
  const parsedImages = JSON.parse(images);
  const bucketUrl = endpoints.bucket;
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(
    BREAKPOINTS.TABLET_LANDSCAPE
  );
  const videoExtensions = VIDEO_EXTENSIONS;

  const [selectedSlideIndex, setSelectedSlideIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const isVideo = (src) =>
    typeof src === 'string' &&
    videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));

  const officialMedia = parsedImages.filter(
    (media) => typeof media === 'string' || media.provider === 'partner'
  );

  // Reorder images for mobile: move cover_image to the beginning
  const reorderedImagesForMobile = useMemo(() => {
    if (!coverImage) return parsedImages;

    // Find the index of the cover_image in the gallery
    const coverImageIndex = parsedImages.findIndex((media) => {
      const mediaUrl = typeof media === 'string' ? media : media.url;
      return mediaUrl === coverImage;
    });

    // If cover_image is not in the gallery or already first, return original array
    if (coverImageIndex <= 0) return parsedImages;

    // Move cover_image to the beginning
    const reordered = [...parsedImages];
    const [coverImageItem] = reordered.splice(coverImageIndex, 1);
    reordered.unshift(coverImageItem);

    return reordered;
  }, [parsedImages, coverImage]);

  const openGallery = (index) => {
    setSelectedSlideIndex(index);
    if (isDeviceSmallerThanTablet) {
      blockScrollMobile();
    } else {
      blockScrollDesktop();
    }
    setIsOpen(true);
  };

  const closeGallery = () => {
    setSelectedSlideIndex(null);
    if (isDeviceSmallerThanTablet) {
      restoreScrollMobile();
    } else {
      restoreScrollDesktop();
    }
  };

  const renderSwiperSlides = useMemo(() => {
    const imagesToRender = isDeviceSmallerThanTablet ? reorderedImagesForMobile : parsedImages;
    return imagesToRender.map((media, i) => {
      const mediaVideo = media.is_video || isVideo(media);
      const urlBody = typeof media === 'string' ? media : media.url;
      const mediaUrl = mediaVideo
        ? useGetVideoPreview(media.url)
        : `${bucketUrl}${urlBody}`;

      return (
        <SwiperSlide key={i}>
          <div
            onClick={() => openGallery(i)}
            style={{ cursor: 'pointer', width: '100%', height: '100%' }}
          >
            <div className="relative">
              <HeadlineListing
                listing={{
                  cover_image: mediaUrl,
                  cover_image_precrop: i === 0 ? coverImagePrecrop : null,
                  available: listingIsAvailable,
                  title: title,
                }}
              />
              {mediaVideo && (
                <div className="cover-swiper-video-component__play-icon">
                  <BetaIcon
                    name="play"
                    color={IconColors.ghost}
                    size={IconSizes[40]}
                  />
                </div>
              )}
            </div>
          </div>
        </SwiperSlide>
      );
    });
  }, [parsedImages, reorderedImagesForMobile, bucketUrl, title, isDeviceSmallerThanTablet, openGallery]);

  if (!isDeviceSmallerThanTablet) {
    return (
      <div className="relative">
        <HeadlineListing
          onClick={(index) => openGallery(index)}
          classes="cover-swiper__headline-listing cover-swiper__headline-listing-grid"
          listing={{
            cover_image: `${bucketUrl}${coverImage}`,
            cover_image_precrop: coverImagePrecrop,
            available: listingIsAvailable,
            title: title,
            mosaic: mosaic,
            offsetCustomer: officialMedia.length,
          }}
          setSelectedSlideIndex={setSelectedSlideIndex}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />

        <div className="cover-swiper__desktop-wrapper">
          <div className="d-flex gap-s z-index-1">
            {!!listingIsAvailable ? (
              <>
                <div className="cover-swiper__actions__icon">
                  <Share title={title} text={shareText} />
                </div>
                <div className="cover-swiper__actions__icon">
                  <AddToWishList
                    listingId={listingId}
                    className="cover-swiper__actions__wish-list"
                    activityName={title}
                    category={category || null}
                    macroCategory={macroCategory || null}
                  />
                </div>
              </>
            ) : (
              <span class="badge badge--secondary badge--large">
                {TRANSLATIONS.NOT_AVAILABLE}
              </span>
            )}
          </div>

          {!!listingIsAvailable && (
            <div className="z-index-1">
              <Slideshow
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                images={images}
                title={title}
                initialIndex={selectedSlideIndex}
                onClose={() => closeGallery()}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cover-swiper">
      {listingIsAvailable ? (
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          modules={[Navigation]}
          navigation
        >
          {renderSwiperSlides}
        </Swiper>
      ) : (
        <HeadlineListing
          listing={{
            cover_image: `${bucketUrl}${coverImage}`,
            cover_image_precrop: coverImagePrecrop,
            available: listingIsAvailable,
            title: title,
          }}
        />
      )}

      <div className="cover-swiper__actions">
        {!!listingIsAvailable ? (
          <>
            <div className="cover-swiper__actions__icon">
              <Share title={title} text={shareText} />
            </div>
            <div className="cover-swiper__actions__icon">
              <AddToWishList
                listingId={listingId}
                className="cover-swiper__actions__wish-list"
                activityName={title}
                category={category || null}
                macroCategory={macroCategory || null}
              />
            </div>
          </>
        ) : (
          <span class="badge badge--secondary badge--large">
            {TRANSLATIONS.NOT_AVAILABLE}
          </span>
        )}
      </div>

      {!!listingIsAvailable && (
        <div className="cover-swiper__toggle-slide-show">
          <Slideshow
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            images={isDeviceSmallerThanTablet ? JSON.stringify(reorderedImagesForMobile) : images}
            title={title}
            initialIndex={selectedSlideIndex}
            onClose={() => closeGallery()}
          />
        </div>
      )}
    </div>
  );
};

export default CoverSwiper;
